import Service from '@ember/service';
import { OpenFeature, type Client } from '@openfeature/web-sdk';
import YepNopeProvider, {
  type YepNopeFlagConfig,
} from 'dashboard/utils/yepnope-openfeature-provider';

export default class FeaturesService extends Service {
  _client: Client;

  /**
   * Setup the feature flags for the application.
   * MUST be called before any other method, including `isEnabled()`.
   */
  async setup(flags: YepNopeFlagConfig): Promise<Client> {
    // TODO: configure `@openfeature/multi-provider-web` when we adopt company-wide feature flags
    /** @see https://github.com/open-feature/js-sdk-contrib/tree/main/libs/providers/multi-provider-web */

    await OpenFeature.setProviderAndWait(new YepNopeProvider(flags));

    // A Client bound to the default provider
    this._client = OpenFeature.getClient();
    return this._client;
  }

  /**
   * Check if a feature is enabled. Should only be used for boolean flags.
   */
  isEnabled(feature: string): boolean {
    if (!this._client) {
      throw new Error(
        'FeaturesService.setup() must be called before using isEnabled()'
      );
    }
    let res: boolean;
    try {
      res = this._client.getBooleanValue(feature, false);
    } catch (error) {
      console.error(`Error getting feature flag ${feature}: ${error}`);
      return false;
    }

    return res;
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:features')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('features') declare altName: FeaturesService;`.
declare module '@ember/service' {
  interface Registry {
    features: FeaturesService;
  }
}
